import * as i0 from '@angular/core';
import { InjectionToken, Inject, Injectable, inject, PLATFORM_ID, APP_INITIALIZER, makeEnvironmentProviders, isDevMode, Pipe, signal, input, computed, ChangeDetectionStrategy, Component, NgModule, ENVIRONMENT_INITIALIZER } from '@angular/core';
import * as i1 from '@jsverse/transloco';
import { getBrowserLang, TranslocoService, translate, isObject, TranslocoPipe, TranslocoModule, provideTransloco } from '@jsverse/transloco';
import { __decorate } from 'tslib';
import { Bind } from '@noda-lib/utils/decorators';
import { replaceTags, selectObjectProps, isNullish, isJSType, isNotNullish, assign, isEqualJSON, excludeObjProps } from '@noda-lib/utils/handlers';
import * as i1$1 from '@angular/common';
import { isPlatformServer, isPlatformBrowser, CommonModule } from '@angular/common';
import { BehaviorSubject, of, shareReplay, firstValueFrom, map as map$1, forkJoin } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { ajax } from 'rxjs/ajax';
import { catchError, switchMap, map } from 'rxjs/operators';
import { getDataFromDB } from '@noda-lib/utils/db';
import { EqualPipe } from '@noda-lib/utils/pipes';
import { provideTranslocoMessageformat } from '@jsverse/transloco-messageformat';
function OptionalTranslateComponent_Case_0_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵtext(0);
    i0.ɵɵpipe(1, "translate");
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵtextInterpolate1(" ", i0.ɵɵpipeBind3(1, 1, ctx_r0.translate(), null, ctx_r0.translateService.lang()), " ");
  }
}
function OptionalTranslateComponent_Case_1_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵtext(0);
    i0.ɵɵpipe(1, "translate");
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵtextInterpolate1(" ", i0.ɵɵpipeBind3(1, 1, ctx_r0.translate().key, ctx_r0.translate().params, ctx_r0.translateService.lang()), " ");
  }
}
function OptionalTranslateComponent_Case_2_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵtext(0);
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵtextInterpolate1(" ", ctx_r0.translate(), " ");
  }
}
class Translate {
  constructor(key, params = null) {
    this.key = key;
    this.params = params;
  }
}
const TRANSLATE_DB = new InjectionToken('TRANSLATE_DB', {
  factory() {
    return {};
  }
});
const TRANSLATE_CONFIG = new InjectionToken('TRANSLATE_CONFIG');
let TranslateDBService = /*#__PURE__*/(() => {
  class TranslateDBService {
    constructor(config) {
      this.config = config;
      this.translateDB$ = new BehaviorSubject(null);
    }
    initTranslateDB(db) {
      this.setTranslateDB(db);
    }
    getTranslateDB() {
      return this.translateDB$.getValue();
    }
    getTranslateMap(language) {
      return this.getTranslateDB()[language];
    }
    setTranslateDB(translateDB) {
      this.translateDB$.next(translateDB);
    }
    isExistDBLang(lang) {
      return lang in this.getTranslateDB();
    }
    static {
      this.ɵfac = function TranslateDBService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || TranslateDBService)(i0.ɵɵinject(TRANSLATE_CONFIG));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: TranslateDBService,
        factory: TranslateDBService.ɵfac
      });
    }
  }
  return TranslateDBService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let TranslocoHttpLoader = /*#__PURE__*/(() => {
  class TranslocoHttpLoader {
    constructor() {
      this.translateDBService = inject(TranslateDBService);
      this.config = inject(TRANSLATE_CONFIG);
      this.http = inject(HttpClient);
      this.isServer = isPlatformServer(inject(PLATFORM_ID));
    }
    getTranslation(lang) {
      return this.getTranslationFromUrl(lang).pipe(catchError(() => of(this.translateDBService.getTranslateMap(this.config.fallbackLang))));
    }
    getTranslationFromUrl(lang) {
      return this.getVersion().pipe(switchMap(version => {
        const baseUrl = this.getTranslationUrl(lang);
        const url = `${baseUrl}?v=${version}`;
        return this.isServer ? this.http.get(url) : ajax.getJSON(url);
      }));
    }
    getVersion() {
      if (!this.version$) {
        const versionUrl = `${this.getUrl()}/version.json`;
        const request$ = this.isServer ? this.http.get(versionUrl) : ajax.getJSON(versionUrl);
        this.version$ = request$.pipe(map(data => data?.version || Date.now().toString()), catchError(() => of(Date.now().toString())), shareReplay(1));
      }
      return this.version$;
    }
    getTranslationUrl(lang) {
      return `${this.getUrl()}/${lang}.json`;
    }
    getUrl() {
      return this.config.baseUrl;
    }
    static {
      this.ɵfac = function TranslocoHttpLoader_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || TranslocoHttpLoader)();
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: TranslocoHttpLoader,
        factory: TranslocoHttpLoader.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return TranslocoHttpLoader;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let TranslocoStaticLoader = /*#__PURE__*/(() => {
  class TranslocoStaticLoader {
    constructor() {
      this.translateDBService = inject(TranslateDBService);
    }
    getTranslation(lang) {
      return of(this.translateDBService.getTranslateMap(lang));
    }
    static {
      this.ɵfac = function TranslocoStaticLoader_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || TranslocoStaticLoader)();
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: TranslocoStaticLoader,
        factory: TranslocoStaticLoader.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return TranslocoStaticLoader;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
function getLanguageToLoad(config, platformId) {
  if (!isPlatformBrowser(platformId)) return config.defaultLang;
  const browserLang = getBrowserLang();
  return browserLang && config.availableLangs.includes(browserLang) ? browserLang : config.defaultLang;
}
function createTranslateInitializer(loadFn) {
  const initializeProvider = {
    provide: APP_INITIALIZER,
    multi: true,
    useFactory() {
      const translocoService = inject(TranslocoService);
      const config = inject(TRANSLATE_CONFIG);
      const platformId = inject(PLATFORM_ID);
      return () => firstValueFrom(loadFn(translocoService, config, platformId).pipe(map$1(() => void 0)));
    }
  };
  return makeEnvironmentProviders([initializeProvider]);
}
const STRATEGY_MAP = {
  runtime: {
    loader: TranslocoHttpLoader,
    validate: config => {
      validateLanguageConfig(config);
    },
    initializer: () => createTranslateInitializer((service, config, platformId) => {
      const langToLoad = getLanguageToLoad(config, platformId);
      return service.load(langToLoad);
    })
  },
  static: {
    loader: TranslocoStaticLoader,
    validate: config => {
      validateDBLangs(config);
      validateDefaultLanguage(config);
      validateTranslateDB(config);
      mergeChildTranslates(config);
    },
    initializer: () => createTranslateInitializer((service, config) => {
      const loads$ = config.availableLangs.map(lang => service.load(lang));
      return forkJoin(loads$);
    })
  }
};
const TranslateLibDB = {
  messages: {
    'unavailable-set-lang': 'Warning (unavailable-set-lang): Can\'t set language to "{{lang}}". This language isn\'t available. Available langs: {{availableLangs}}. Fallback language in currently used: "{{fallbackLang}}"',
    'invalid-available-langs': 'Warning (invalid-available-langs): Property "availableLangs" in translate config is incorrect. Language "{{lang}}" in translate db isn\'t exist.',
    'unavailable-default-lang': 'Warning (unavailable-default-lang): Property "defaultLang" in translate config is incorrect. Language "{{defaultLang}}" isn\'t available. Available langs: {{availableLangs}}',
    // *Library messages
    'library-matches-keys': 'Library "{{name}}" contains equal keys with application translate db. Equal keys: {{matchesKeys}}. You should rename this keys.',
    'library-unavailable-lang': 'Library "{{name}}" doesn\'t contains translate for "{{lang}}" language',
    'library-translate-keys-difference': 'Library "{{libName}}" contains differences for language "{{lang}}" in the following keys: "{{differenceKeys}}". Please synchronize translates for the "{{libName}}" library within "{{appName}}" application'
  }
};
function logMessage(key, params, type = 'warn') {
  const message = getDataFromDB(TranslateLibDB, ['messages', key]);
  console[type](replaceTags(message, params));
}
function invalidAvailableLang(lang) {
  logMessage('invalid-available-langs', {
    lang
  });
  throw new Error(`Translate merge error (invalid-available-langs): remove language "${lang}" from config.availableLangs or add translate map for "${lang}" language`);
}
function createTranslateDB(langs) {
  return langs.reduce((accum, lang) => {
    accum[lang] = {};
    return accum;
  }, {});
}
function validate(config) {
  STRATEGY_MAP[config.strategy].validate(config);
}
function validateDBLangs(config) {
  const {
    availableLangs,
    db,
    defaultLang
  } = config;
  if (db) {
    config.availableLangs = availableLangs.filter(lang => {
      const isAvailableLang = lang in db;
      if (!isAvailableLang && !isDevMode()) {
        invalidAvailableLang(lang);
      }
      return isAvailableLang;
    });
  } else {
    config.availableLangs = [defaultLang];
  }
}
function validateLanguageConfig(config) {
  const {
    availableLangs,
    fallbackLang
  } = config;
  if (!availableLangs.includes(config.defaultLang)) {
    config.defaultLang = fallbackLang;
  }
}
function validateDefaultLanguage(config) {
  const {
    availableLangs,
    defaultLang,
    fallbackLang
  } = config;
  const isAvailableDefaultLang = availableLangs.includes(defaultLang);
  const isBrowserDefaultLang = defaultLang === getBrowserLang();
  if (!isAvailableDefaultLang) {
    config.defaultLang = fallbackLang;
    if (!isBrowserDefaultLang) logMessage('unavailable-default-lang', {
      defaultLang,
      availableLangs
    });
  }
}
function validateTranslateDB(config) {
  const {
    db,
    availableLangs
  } = config;
  if (db) {
    const translateDB = selectObjectProps(db, config.availableLangs);
    config.db = structuredClone(translateDB);
  } else {
    config.db = createTranslateDB(availableLangs);
  }
}
function mergeChildTranslates(config) {
  const {
    db: rootTranslateDB,
    defaultLang,
    mergeWith
  } = config;
  if (mergeWith) {
    const translateDB = rootTranslateDB ?? {
      [defaultLang]: {}
    };
    mergeWith.forEach(manager => manager.merge(translateDB));
  }
}
// * Public
function getTranslate(key, params = null, lang) {
  return translate(key, params, lang);
}
function extractTranslates(db, modules, langs = Object.keys(db)) {
  const libTranslate = langs.reduce((accum, lang) => {
    const translates = selectObjectProps(db[lang], modules);
    accum[lang] = translates;
    return accum;
  }, {});
  return JSON.parse(JSON.stringify(libTranslate));
}
function getTranslateValueByPath(key, languageMap) {
  if (isNullish(key) || !isJSType(key, 'string')) return null;
  const translateValue = key.split('.').reduce((map, k) => map?.[k], languageMap);
  return translateValue;
}
function isTranslatePath(key, languageMap) {
  return isNotNullish(getTranslateValueByPath(key, languageMap));
}
function isTranslateKey(key, languageMap) {
  const value = getTranslateValueByPath(key, languageMap);
  return isNotNullish(value) && isJSType(value, 'string');
}
function isTranslateModel(object) {
  return isNotNullish(object) && isObject(object) && Object.keys(new Translate(null)).every(k => k in object);
}
function isOptionalTranslate(value, languageMap) {
  const isString = isJSType(value, 'string');
  return isString || isTranslateKey(value, languageMap) || isTranslateModel(value);
}
let TranslatePipe = /*#__PURE__*/(() => {
  class TranslatePipe extends TranslocoPipe {
    transform(value, params = null, lang) {
      return super.transform(value, params, lang);
    }
    static {
      this.ɵfac = /* @__PURE__ */(() => {
        let ɵTranslatePipe_BaseFactory;
        return function TranslatePipe_Factory(__ngFactoryType__) {
          return (ɵTranslatePipe_BaseFactory || (ɵTranslatePipe_BaseFactory = i0.ɵɵgetInheritedFactory(TranslatePipe)))(__ngFactoryType__ || TranslatePipe);
        };
      })();
    }
    static {
      this.ɵpipe = /* @__PURE__ */i0.ɵɵdefinePipe({
        name: "translate",
        type: TranslatePipe,
        pure: true
      });
    }
  }
  return TranslatePipe;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let TranslateService = /*#__PURE__*/(() => {
  class TranslateService {
    constructor(config, translocoService, translateDBService) {
      this.config = config;
      this.translocoService = translocoService;
      this.translateDBService = translateDBService;
      this._lang = signal(this.translocoService.getActiveLang()); // ! Дождаться пока transloco переедет на сигналы для поля lang и удалить это поле с заменой на transloco.lang https://github.com/jsverse/transloco/blob/master/libs/transloco/src/lib/transloco.service.ts
      this.lang = this._lang.asReadonly();
    }
    getTranslate(key, params = null, lang = this.translocoService.getActiveLang()) {
      return this.translocoService.translate(key, params, lang);
    }
    getTranslate$(key, params, lang) {
      return this.translocoService.selectTranslate(key, params, lang);
    }
    getOptionalTranslate(value, lang) {
      switch (true) {
        case this.isTranslateKey(value):
          {
            return this.getTranslate(value, null, lang);
          }
        case isTranslateModel(value):
          {
            const {
              key,
              params
            } = value;
            return this.getTranslate(key, params, lang);
          }
        default:
          {
            return value;
          }
      }
    }
    getActiveLang() {
      return this.translocoService.getActiveLang();
    }
    langChanges() {
      return this.translocoService.langChanges$;
    }
    setLanguage(candidateLang) {
      const {
        fallbackLang
      } = this.config;
      const availableLangs = this.translocoService.getAvailableLangs();
      const isAvailableLanguage = availableLangs.includes(candidateLang) && this.translateDBService.isExistDBLang(candidateLang);
      const language = isAvailableLanguage ? candidateLang : fallbackLang;
      if (!isAvailableLanguage) logMessage('unavailable-set-lang', {
        lang: candidateLang,
        availableLangs,
        fallbackLang
      });
      this.translocoService.setActiveLang(language);
      this._lang.set(language);
    }
    isTranslatePath(key) {
      const translateMap = this.translateDBService.getTranslateMap(this.getActiveLang());
      return isTranslatePath(key, translateMap);
    }
    isTranslateKey(key) {
      const lang = this.config.strategy === 'runtime' ? this.config.fallbackLang : this.getActiveLang();
      const translateMap = this.translateDBService.getTranslateMap(lang);
      return isTranslateKey(key, translateMap);
    }
    isOptionalTranslate(value) {
      const translateMap = this.translateDBService.getTranslateMap(this.getActiveLang());
      return isOptionalTranslate(value, translateMap);
    }
    static {
      this.ɵfac = function TranslateService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || TranslateService)(i0.ɵɵinject(TRANSLATE_CONFIG), i0.ɵɵinject(i1.TranslocoService), i0.ɵɵinject(TranslateDBService));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: TranslateService,
        factory: TranslateService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return TranslateService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
class OptionalTranslateComponent {
  constructor() {
    this.translateService = inject(TranslateService);
    this.translate = input.required();
    this.type = computed(this.computedTranslate);
  }
  computedTranslate() {
    switch (true) {
      case this.translateService.isTranslateKey(this.translate()):
        {
          return 'translate-key';
        }
      case isTranslateModel(this.translate()):
        {
          return 'translate-model';
        }
      default:
        {
          return 'string';
        }
    }
  }
  static {
    this.ɵfac = function OptionalTranslateComponent_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || OptionalTranslateComponent)();
    };
  }
  static {
    this.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
      type: OptionalTranslateComponent,
      selectors: [["i18n-optional-translate"]],
      inputs: {
        translate: [1, "translate"]
      },
      decls: 3,
      vars: 1,
      template: function OptionalTranslateComponent_Template(rf, ctx) {
        if (rf & 1) {
          i0.ɵɵconditionalCreate(0, OptionalTranslateComponent_Case_0_Template, 2, 5)(1, OptionalTranslateComponent_Case_1_Template, 2, 5)(2, OptionalTranslateComponent_Case_2_Template, 1, 1);
        }
        if (rf & 2) {
          let tmp_0_0;
          i0.ɵɵconditional((tmp_0_0 = ctx.type()) === "translate-key" ? 0 : tmp_0_0 === "translate-model" ? 1 : 2);
        }
      },
      dependencies: [TranslatePipe],
      encapsulation: 2,
      changeDetection: 0
    });
  }
}
__decorate([Bind()], OptionalTranslateComponent.prototype, "computedTranslate", null);
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
const TranslateImports = [CommonModule, TranslocoPipe, EqualPipe, TranslatePipe];
let TranslateComponent = /*#__PURE__*/(() => {
  class TranslateComponent {
    constructor() {
      this.translateService = inject(TranslateService);
      this.key = input.required();
      this.params = input(null);
      this.language = input(null);
    }
    static {
      this.ɵfac = function TranslateComponent_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || TranslateComponent)();
      };
    }
    static {
      this.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
        type: TranslateComponent,
        selectors: [["i18n-translate"]],
        inputs: {
          key: [1, "key"],
          params: [1, "params"],
          language: [1, "language"]
        },
        decls: 2,
        vars: 5,
        template: function TranslateComponent_Template(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵtext(0);
            i0.ɵɵpipe(1, "translate");
          }
          if (rf & 2) {
            i0.ɵɵtextInterpolate1("", i0.ɵɵpipeBind3(1, 1, ctx.key(), ctx.params(), ctx.translateService.lang()), "\n");
          }
        },
        dependencies: [i1$1.CommonModule, TranslatePipe],
        encapsulation: 2,
        changeDetection: 0
      });
    }
  }
  return TranslateComponent;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let OptionalTranslatePipe = /*#__PURE__*/(() => {
  class OptionalTranslatePipe {
    constructor() {
      this.translateService = inject(TranslateService);
    }
    transform(value, lang) {
      return this.translateService.getOptionalTranslate(value, lang);
    }
    static {
      this.ɵfac = function OptionalTranslatePipe_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || OptionalTranslatePipe)();
      };
    }
    static {
      this.ɵpipe = /* @__PURE__ */i0.ɵɵdefinePipe({
        name: "optionalTranslate",
        type: OptionalTranslatePipe,
        pure: true
      });
    }
  }
  return OptionalTranslatePipe;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/* eslint-disable @angular-eslint/no-pipe-impure */
let OptionalTranslateAsyncPipe = /*#__PURE__*/(() => {
  class OptionalTranslateAsyncPipe extends OptionalTranslatePipe {
    static {
      this.ɵfac = /* @__PURE__ */(() => {
        let ɵOptionalTranslateAsyncPipe_BaseFactory;
        return function OptionalTranslateAsyncPipe_Factory(__ngFactoryType__) {
          return (ɵOptionalTranslateAsyncPipe_BaseFactory || (ɵOptionalTranslateAsyncPipe_BaseFactory = i0.ɵɵgetInheritedFactory(OptionalTranslateAsyncPipe)))(__ngFactoryType__ || OptionalTranslateAsyncPipe);
        };
      })();
    }
    static {
      this.ɵpipe = /* @__PURE__ */i0.ɵɵdefinePipe({
        name: "optionalTranslate$",
        type: OptionalTranslateAsyncPipe,
        pure: false
      });
    }
  }
  return OptionalTranslateAsyncPipe;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/* eslint-disable @angular-eslint/no-pipe-impure */
let TranslateAsyncPipe = /*#__PURE__*/(() => {
  class TranslateAsyncPipe extends TranslatePipe {
    static {
      this.ɵfac = /* @__PURE__ */(() => {
        let ɵTranslateAsyncPipe_BaseFactory;
        return function TranslateAsyncPipe_Factory(__ngFactoryType__) {
          return (ɵTranslateAsyncPipe_BaseFactory || (ɵTranslateAsyncPipe_BaseFactory = i0.ɵɵgetInheritedFactory(TranslateAsyncPipe)))(__ngFactoryType__ || TranslateAsyncPipe);
        };
      })();
    }
    static {
      this.ɵpipe = /* @__PURE__ */i0.ɵɵdefinePipe({
        name: "translate$",
        type: TranslateAsyncPipe,
        pure: false
      });
    }
  }
  return TranslateAsyncPipe;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
const imports = [TranslocoModule, TranslateComponent, TranslatePipe, TranslateAsyncPipe];
let TranslateModule = /*#__PURE__*/(() => {
  class TranslateModule {
    static {
      this.ɵfac = function TranslateModule_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || TranslateModule)();
      };
    }
    static {
      this.ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
        type: TranslateModule
      });
    }
    static {
      this.ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({
        imports: [TranslocoModule, TranslateComponent, TranslocoModule]
      });
    }
  }
  return TranslateModule;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
const DEFAULT_LANGUAGE = 'en';
const DEFAULT_AVAILABLE_LANGS = [DEFAULT_LANGUAGE];
const TRANSLOCO_DEFAULT_OPTIONS = {
  defaultLang: getBrowserLang(),
  reRenderOnLangChange: true,
  prodMode: !isDevMode(),
  availableLangs: DEFAULT_AVAILABLE_LANGS
};
const TRANSLATE_DEFAULT_OPTIONS = {
  db: null
};
function missingLibraryLanguageError(libName, lang) {
  logMessage('library-unavailable-lang', {
    name: libName,
    lang
  }, 'error');
  throw new Error(`Translate merge error (library-unavailable-lang): add translate language "${lang}" for library "${libName}"`);
}
function libraryMatchesKeysError(libName, matchesKeys) {
  logMessage('library-matches-keys', {
    name: libName,
    matchesKeys
  }, 'error');
  throw new Error(`Translate merge error (library-matches-keys): contains equal keys for library "${libName}".`);
}
function libraryDifferenceKeysError(libName, appName, differenceKeys, lang) {
  logMessage('library-translate-keys-difference', {
    libName,
    appName,
    differenceKeys,
    lang
  }, 'error');
  throw new Error(`Translate merge error (library-translate-keys-difference): contains difference translate for keys "${differenceKeys}" in library "${libName}". Application: "${appName}", language: "${lang}"`);
}
class MergeTranslateManager {
  constructor(translateName, translateMap) {
    this.translateName = translateName;
    this.translateMap = translateMap;
    this.translateMap = structuredClone(translateMap);
  }
  merge(rootDB) {
    Object.keys(rootDB).forEach(lang => {
      const rootTranslateByLang = rootDB[lang];
      const childTranslateByLang = this.translateMap[lang];
      if (!childTranslateByLang) {
        missingLibraryLanguageError(this.translateName, lang);
      } else {
        const matchesKeys = this.validateKeysMatches(rootTranslateByLang, childTranslateByLang);
        if (matchesKeys.length) {
          libraryMatchesKeysError(this.translateName, matchesKeys);
        }
        const langTranslateDB = assign(rootTranslateByLang, childTranslateByLang);
        rootDB[lang] = langTranslateDB;
      }
    });
  }
  validateKeysMatches(rootDB, childDB) {
    const rootDBKeys = Object.keys(rootDB);
    const childDBKeys = Object.keys(childDB);
    return childDBKeys.filter(key => rootDBKeys.includes(key));
  }
}
class MergeTranslatesManager {
  constructor(translateName, translateMaps) {
    this.translateName = translateName;
    this.translateMaps = translateMaps;
    this.translateMaps = structuredClone(translateMaps);
  }
  merge(rootDB) {
    const langs = Object.keys(rootDB);
    const childrenMergedDB = this.mergeChildrenTranslate(this.translateMaps, langs);
    const baseMergeManager = new MergeTranslateManager(this.translateName, childrenMergedDB);
    baseMergeManager.merge(rootDB);
  }
  mergeChildrenTranslate(translateMaps, langs) {
    const emptyChildrenDB = createTranslateDB(langs);
    const childrenDB = langs.reduce((accum, lang) => {
      translateMaps.forEach(child => this.mergeChildTranslate(accum, child, lang));
      return accum;
    }, emptyChildrenDB);
    return childrenDB;
  }
  mergeChildTranslate(baseDB, child, lang) {
    const {
      db: childDB,
      name: libName
    } = child;
    const fullLibName = `${this.translateName} => ${libName}`;
    const baseTranslateByLang = baseDB[lang];
    const childTranslateByLang = childDB[lang];
    this.validateLanguage(childTranslateByLang, fullLibName, lang);
    this.validateKeys(baseTranslateByLang, childTranslateByLang, libName, lang);
    Object.assign(baseTranslateByLang, childTranslateByLang);
  }
  validateLanguage(translateMap, libName, lang) {
    const isExistLanguageTranslate = Boolean(translateMap);
    if (!isExistLanguageTranslate) {
      missingLibraryLanguageError(libName, lang);
    }
  }
  validateKeys(baseMap, childMap, libName, lang) {
    const differenceKeys = Object.keys(childMap).filter(childKey => {
      const isExistKeyInBase = childKey in baseMap;
      if (isExistKeyInBase) {
        const baseMapModule = baseMap[childKey];
        const childMapModule = childMap[childKey];
        const isEqualModules = isEqualJSON(baseMapModule, childMapModule);
        return !isEqualModules;
      }
      return false;
    });
    if (Boolean(differenceKeys.length)) {
      libraryDifferenceKeysError(libName, this.translateName, differenceKeys, lang);
    }
  }
}
function addTranslocoProvider(providers, config) {
  const strategy = STRATEGY_MAP[config.strategy || 'static'];
  const translocoCfg = excludeObjProps(config, ['db']);
  const translocoOptions = {
    loader: strategy.loader,
    config: translocoCfg
  };
  providers.push(provideTransloco(translocoOptions), provideTranslocoMessageformat(), strategy.initializer());
}
function makeConfig(candidate) {
  const config = assign(TRANSLOCO_DEFAULT_OPTIONS, TRANSLATE_DEFAULT_OPTIONS, candidate);
  validate(config);
  return config;
}
function addConfigProvider(providers, config) {
  const provider = {
    provide: TRANSLATE_CONFIG,
    useValue: config
  };
  providers.push(provider);
}
function addDBProvider(providers, config) {
  const {
    db
  } = config;
  const translateDBProvider = {
    provide: TRANSLATE_DB,
    useValue: db
  };
  const initDBProvider = makeEnvironmentProviders([{
    provide: ENVIRONMENT_INITIALIZER,
    useValue() {
      const dbService = inject(TranslateDBService);
      dbService.initTranslateDB(db);
    },
    multi: true
  }]);
  providers.push(translateDBProvider, initDBProvider);
}
// * Providers
function withChildTranslate(db, dbName) {
  return new MergeTranslateManager(dbName, db);
}
function withChildTranslates(dbs, libraryName) {
  return new MergeTranslatesManager(libraryName, dbs);
}
function provideTranslate(config) {
  const cfg = makeConfig(config);
  const providers = [TranslateService, TranslateDBService];
  addConfigProvider(providers, cfg);
  addDBProvider(providers, cfg);
  addTranslocoProvider(providers, cfg);
  return makeEnvironmentProviders(providers);
}

/*
 * Public API Surface of translate
 */

/**
 * Generated bundle index. Do not edit.
 */

export { OptionalTranslateAsyncPipe, OptionalTranslateComponent, OptionalTranslatePipe, TRANSLATE_CONFIG, TRANSLATE_DB, Translate, TranslateAsyncPipe, TranslateComponent, TranslateModule, TranslatePipe, TranslateService, extractTranslates, getTranslate, isOptionalTranslate, isTranslateKey, isTranslateModel, isTranslatePath, provideTranslate, withChildTranslate, withChildTranslates };
